import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { INLINE_CTA } from "../../lib/blog-markers";

export const eviterDoublonsCadeauxNoel: BlogPost = {
  h1: "Éviter les cadeaux en double à Noël : les solutions qui marchent vraiment",
  seoTitle: "Éviter les cadeaux en double à Noël : 6 solutions simples · Listou",
  seoDescription:
    "Deux fois le même jouet sous le sapin ? Pourquoi les doublons arrivent, et comment les éviter : liste partagée avec réservation, cadeaux à plusieurs, grands-parents, enfants, et que faire du doublon reçu.",
  publishedOn: "2026-10-26",
  updatedOn: "2026-10-26",
  summary:
    "Les cadeaux en double arrivent parce que chacun achète de son côté, à partir des mêmes indices. La solution la plus sûre est une liste partagée où chacun réserve le cadeau qu'il offre : tout le monde voit qu'il est pris, sans que la personne qui reçoit le sache. Pour les gros cadeaux, on se met à plusieurs plutôt que d'acheter chacun une petite variante.",
  body: `
## Pourquoi les doublons arrivent

Le scénario est toujours le même. L'enfant a parlé du même jeu à ses deux grands-mères. La tante et l'oncle ont lu le même message « elle aimerait bien un livre de cuisine ». Chacun achète de son côté, de bonne foi, et le 24 au soir, deux paquets ont la même forme.

Trois causes reviennent :

- **les mêmes indices pour tout le monde** : une envie dite à voix haute circule dans toute la famille ;
- **personne ne sait ce que les autres ont acheté** : on n'ose pas demander, pour ne pas gâcher la surprise ;
- **les achats tardifs** : la dernière semaine, on prend ce qui reste en rayon, souvent le jouet de la vitrine.

## Solution 1 : une liste partagée avec réservation

C'est la seule solution qui règle le problème à la source. La personne qui reçoit fait sa liste ; chaque proche **réserve** le cadeau qu'il offre ; les autres voient qu'il est pris et en choisissent un autre.

Ce qui fait une bonne liste anti-doublons :

- **la réservation visible pour les proches**, pas seulement un tableau que l'organisateur tient à la main ;
- **la surprise gardée** : la personne qui reçoit ne voit pas ce qui a été réservé sur sa propre liste ;
- **pas de compte à créer** pour réserver, sinon une partie de la famille ne le fera pas ;
- **des quantités** : trois tomes d'une série, deux paires de chaussons, chacun réservé séparément.

Sur [Listou](/liste-de-noel), un cadeau réservé l'est pour tout le monde, et vous ne voyez pas ce qui a été pris sur votre liste.
${INLINE_CTA}
## Solution 2 : un seul lien pour toute la famille

À Noël, le doublon vient souvent d'une liste qu'on n'a pas vue. Avec une liste par personne, quelqu'un oublie toujours d'envoyer la sienne, ou de regarder celle du cousin.

Un **groupe de listes** réunit tout le monde derrière un seul lien : chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. Les [listes en groupe](/listes-en-groupe) de Listou fonctionnent ainsi, sans compte pour personne.

## Solution 3 : les gros cadeaux à plusieurs

Quand trois personnes veulent offrir « quelque chose pour la console », on se retrouve avec trois jeux à 15 € au lieu de la manette qu'il voulait. Mieux vaut **un gros cadeau partagé** :

- sur la liste, l'envie porte une **quantité** : une trottinette à 150 € en trois parts de 50 € ;
- chaque proche réserve sa part ;
- celui qui réserve la dernière part, ou le plus proche, passe la commande.

## Solution 4 : parler aux grands-parents

Les grands-parents sont les champions du doublon, souvent parce qu'ils achètent tôt, en magasin, et ne consultent pas toujours la liste. Trois réflexes :

- **leur envoyer la liste en premier**, avant tout le monde : ils ont le premier choix ;
- **réserver à leur place** s'ils vous disent ce qu'ils ont acheté ;
- leur proposer un **cadeau à eux**, hors liste, s'ils préfèrent choisir : un souvenir, une sortie, un livre de leur enfance.

## Solution 5 : la liste des enfants, tenue par les parents

L'enfant change d'avis tous les trois jours, et chaque adulte a entendu une version différente. Les parents tiennent la liste avec lui, retirent ce qui ne lui plaît plus, et la famille n'a qu'une source à consulter. Notre guide des [idées de liste de Noël par âge](/blog/liste-de-noel-enfant-idees-par-age) aide à la remplir.

## Solution 6 : le Père Noël secret entre adultes

Entre adultes, le plus simple est parfois de supprimer la question : chacun tire au sort une seule personne à qui offrir. Moins de cadeaux, mieux choisis, et aucun doublon possible. Voyez notre guide du [Père Noël secret en famille](/blog/pere-noel-secret-en-famille).

## Et si le doublon arrive quand même

Il arrivera peut-être, avec un cadeau hors liste. Quelques usages :

- **Ne le montrez pas** : remerciez les deux personnes, chacune pour son cadeau.
- **Échangez-le discrètement** après les fêtes, sans le dire.
- **Offrez-le à quelqu'un d'autre**, s'il ne s'échange pas, plus tard dans l'année.
- **Notez l'erreur pour l'an prochain** : la liste partagée, envoyée plus tôt, aux grands-parents d'abord.
`,
  faq: [
    [
      "Comment éviter d'offrir deux fois le même cadeau à Noël ?",
      "Avec une liste partagée où chacun réserve le cadeau qu'il offre : les autres proches voient qu'il est pris et en choisissent un autre. Sur Listou, c'est gratuit et sans compte pour ceux qui réservent.",
    ],
    [
      "La personne qui reçoit voit-elle les cadeaux réservés ?",
      "Sur Listou, non : vous ne voyez pas ce qui a été réservé sur votre propre liste. Les proches, eux, voient ce qui est pris, et c'est ce qui évite les doublons.",
    ],
    [
      "Comment faire avec les grands-parents qui ne regardent pas la liste ?",
      "Envoyez-leur la liste en premier, et réservez à leur place quand ils vous disent ce qu'ils ont acheté. S'ils préfèrent choisir eux-mêmes, proposez-leur un cadeau hors liste : un souvenir, une sortie.",
    ],
    [
      "Comment offrir un gros cadeau à plusieurs sans doublon ?",
      "Donnez une quantité à l'envie sur la liste, par exemple trois parts de 50 € pour une trottinette à 150 €. Chaque proche réserve sa part, et personne n'achète une petite variante à côté.",
    ],
    [
      "Que faire d'un cadeau reçu en double ?",
      "Remerciez les deux personnes, chacune pour son cadeau, puis échangez-le discrètement après les fêtes. Personne n'a besoin de le savoir.",
    ],
  ],
  occasion: "christmas",
  inlineCta: {
    title: "Plus jamais deux fois le même cadeau",
    text: "Chacun réserve ce qu'il offre, et vous gardez la surprise. Gratuit, sans compte pour vos proches.",
    sticker: stickerUrl("noel", "cadeau"),
  },
};
